import { PageCanvas } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  RecordDescription,
  RecordMetadata,
  RecordRow,
  RecordTitle,
} from "@/components/record-link";
import { newLocationServicePath, editLocationServicePath } from "@/routes";
import type { Service, Offering } from "./types";
import { FormErrors } from "@/components/forms/form-errors";
import BookingSetupReturn, {
  type BookingReturnContext,
} from "@/components/booking-setup-return";

export default function Services({
  services,
  offerings,
  booking_return,
}: {
  services: Service[];
  offerings: Offering[];
  booking_return?: BookingReturnContext;
}) {
  const { auth, errors } = usePage().props;
  const { t, i18n } = useTranslation();
  if (!auth.location) return null;
  const locationId = auth.location.id;
  const manage = auth.user?.role === "admin";
  const returnParams = booking_return
    ? { booking_return: booking_return.token }
    : {};
  return (
    <AppLayout breadcrumbs={[]}>
      <Head title={t("services.title")} />
      <PageCanvas>
        <BookingSetupReturn context={booking_return} />
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-page font-display">{t("services.title")}</h1>
          {manage && (
            <Button
              render={
                <Link href={newLocationServicePath(locationId, returnParams)} />
              }
            >
              {t("services.add")}
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          {t("services.local_help", { location: auth.location.name })}
        </p>
        <FormErrors errors={errors || {}} />
        <Card>
          <CardContent className="divide-y">
            {services.length === 0 && (
              <p className="py-6 text-muted-foreground">
                {t("services.empty")}
              </p>
            )}
            {services.map((service) => {
              const offering = offerings.find(
                (item) => item.service_id === service.id,
              );
              const editable = manage && !service.archived;
              const summary = (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <h2>
                      {editable ? (
                        <RecordTitle>{service.name}</RecordTitle>
                      ) : (
                        <span className="font-semibold break-words">
                          {service.name}
                        </span>
                      )}
                    </h2>
                    <RecordMetadata>
                      {t(
                        service.archived
                          ? "services.archived"
                          : !offering
                            ? "services.unconfigured"
                            : offering.available
                              ? "services.available"
                              : "services.unavailable",
                      )}
                    </RecordMetadata>
                  </div>
                  {service.description && (
                    <RecordDescription className="max-w-prose whitespace-pre-wrap">
                      {service.description}
                    </RecordDescription>
                  )}
                  {offering && (
                    <div className="space-y-1">
                      <RecordDescription>
                        {new Intl.NumberFormat(i18n.language, {
                          style: "currency",
                          currency: offering.currency,
                        }).format(Number(offering.price))}{" "}
                        ·{" "}
                        {t("services.duration", {
                          count: offering.estimated_minutes,
                        })}
                      </RecordDescription>
                      <RecordMetadata>
                        {t(`services.${offering.tax_mode}`)} (
                        {new Intl.NumberFormat(i18n.language, {
                          maximumFractionDigits: 4,
                        }).format(Number(offering.tax_rate))}
                        %{offering.tax_label ? ` · ${offering.tax_label}` : ""})
                      </RecordMetadata>
                    </div>
                  )}
                  {editable && (
                    <span className="inline-block text-sm text-muted-foreground">
                      {t("services.edit")}
                    </span>
                  )}
                </div>
              );
              return (
                <article key={service.id} className="py-3">
                  {editable ? (
                    <RecordRow
                      href={editLocationServicePath(
                        locationId,
                        service.id,
                        returnParams,
                      )}
                    >
                      {summary}
                    </RecordRow>
                  ) : (
                    <div className="px-3 py-3">{summary}</div>
                  )}
                </article>
              );
            })}
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
